import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { getTowerHelper } from '../utils/towerHelpers';
import DrawingCanvas from './DrawingCanvas';

// Context type definition
interface DrawingCanvasContextType {
  showDrawingCanvas: (towerId: string, prompt: string) => void;
  hideDrawingCanvas: () => void;
  isVisible: boolean;
  currentTowerId: string | null;
  currentPrompt: string | null;
}

// Create context with default values
const DrawingCanvasContext = createContext<DrawingCanvasContextType>({
  showDrawingCanvas: () => {},
  hideDrawingCanvas: () => {},
  isVisible: false,
  currentTowerId: null,
  currentPrompt: null
});

// Hook to use the drawing canvas context
export const useDrawingCanvas = () => useContext(DrawingCanvasContext);

// Props for the provider component
interface DrawingCanvasProviderProps {
  children: ReactNode;
}

// Provider component
export const DrawingCanvasProvider: React.FC<DrawingCanvasProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTowerId, setCurrentTowerId] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<string | null>(null);
  const { isMobile } = useResponsive();
  
  const showDrawingCanvas = useCallback((towerId: string, prompt: string) => {
    console.log('Showing drawing canvas for tower:', towerId, 'with prompt:', prompt);
    setCurrentTowerId(towerId);
    setCurrentPrompt(prompt);
    setIsVisible(true);
    // Debug log
    console.log('Drawing canvas state:', { isVisible: true, currentTowerId: towerId, currentPrompt: prompt });
  }, []);
  
  const hideDrawingCanvas = useCallback(() => {
    console.log('Hiding drawing canvas');
    setIsVisible(false);
    setCurrentTowerId(null);
    setCurrentPrompt(null);
  }, []);
  
  const handleSubmit = useCallback(() => {
    if (currentTowerId) {
      const helper = getTowerHelper(currentTowerId);
      if (helper && helper.handleDrawingSubmit) {
        helper.handleDrawingSubmit();
      }
    }
    hideDrawingCanvas();
  }, [currentTowerId, hideDrawingCanvas]);

  // Debug log for render
  console.log('DrawingCanvasProvider render:', { isVisible, currentTowerId, currentPrompt });
  
  return (
    <DrawingCanvasContext.Provider value={{
      showDrawingCanvas,
      hideDrawingCanvas,
      isVisible,
      currentTowerId,
      currentPrompt
    }}>
      {children}
      
      {isVisible && currentPrompt && (
        <DrawingCanvas 
          isOpen={isVisible}
          onClose={hideDrawingCanvas}
          onSubmit={handleSubmit}
          prompt={currentPrompt}
        />
      )}
    </DrawingCanvasContext.Provider>
  );
}; 